"use client";

import * as React from "react";
import { useState, useRef, useEffect, createContext, useContext } from "react";
import ChevronDownIcon from "@heroicons/react/24/solid/ChevronDownIcon";
import { cn } from "@/lib/utils";

// Context to manage which accordion items are open
const AccordionContext = createContext<{
  openItems: string[];
  toggleItem: (itemId: string) => void;
  type: "single" | "multiple";
}>({
  openItems: [],
  toggleItem: () => {},
  type: "single",
});

interface AccordionProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: "single" | "multiple";
  defaultValue?: string | string[];
  collapsible?: boolean;
  onValueChange?: (value: string | string[]) => void;
}

const Accordion = ({
  children,
  className,
  type = "single",
  defaultValue = [],
  collapsible = false,
  onValueChange,
  ...props
}: AccordionProps) => {
  const [openItems, setOpenItems] = useState<string[]>(
    Array.isArray(defaultValue)
      ? defaultValue
      : defaultValue
        ? [defaultValue]
        : [],
  );

  const toggleItem = (itemId: string) => {
    setOpenItems((prev) => {
      // If the item is already open
      if (prev.includes(itemId)) {
        // If collapsible is true or it's not the only open item, remove it
        if (collapsible || prev.length > 1) {
          const newOpenItems = prev.filter((id) => id !== itemId);
          onValueChange?.(
            type === "single" ? newOpenItems[0] || "" : newOpenItems,
          );
          return newOpenItems;
        }
        return prev;
      }

      // If type is single, replace all open items with this one
      if (type === "single") {
        onValueChange?.(itemId);
        return [itemId];
      }

      // If type is multiple, add this item to the open items
      const newOpenItems = [...prev, itemId];
      onValueChange?.(newOpenItems);
      return newOpenItems;
    });
  };

  return (
    <AccordionContext.Provider value={{ openItems, toggleItem, type }}>
      <div
        className={cn("divide-y divide-gray-secondary", className)}
        {...props}
      >
        {children}
      </div>
    </AccordionContext.Provider>
  );
};

interface AccordionItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
}

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ className, children, value, ...props }, ref) => {
    const { openItems } = useContext(AccordionContext);
    const isOpen = openItems.includes(value);

    return (
      <div
        ref={ref}
        data-state={isOpen ? "open" : "closed"}
        data-value={value}
        className={cn("border-b border-gray-secondary", className)}
        {...props}
      >
        {children}
      </div>
    );
  },
);
AccordionItem.displayName = "AccordionItem";

interface AccordionTriggerProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

const AccordionTrigger = React.forwardRef<
  HTMLButtonElement,
  AccordionTriggerProps
>(({ className, children, ...props }, ref) => {
  const { toggleItem, openItems } = useContext(AccordionContext);
  const itemRef = useRef<HTMLDivElement>(null);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (itemRef.current) {
      const parent = itemRef.current.closest("[data-value]");
      if (parent) {
        const value = parent.getAttribute("data-value") || "";
        toggleItem(value);
      }
    }
  };

  return (
    <div className="flex" ref={itemRef}>
      <button
        ref={ref}
        type="button"
        onClick={handleClick}
        className={cn(
          "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline",
          className,
        )}
        {...props}
      >
        {children}
        <ChevronDownIcon
          className={cn(
            "h-4 w-4 shrink-0 transition-transform duration-200",
            openItems.includes(
              itemRef.current
                ?.closest("[data-value]")
                ?.getAttribute("data-value") || "",
            )
              ? "rotate-180"
              : "",
          )}
        />
      </button>
    </div>
  );
});
AccordionTrigger.displayName = "AccordionTrigger";

interface AccordionContentProps extends React.HTMLAttributes<HTMLDivElement> {}

const AccordionContent = React.forwardRef<
  HTMLDivElement,
  AccordionContentProps
>(({ className, children, ...props }, ref) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const { openItems } = useContext(AccordionContext);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (contentRef.current) {
      const parent = contentRef.current.closest("[data-value]");
      if (parent) {
        const value = parent.getAttribute("data-value") || "";
        const open = openItems.includes(value);
        setIsOpen(open);

        // Set height for animation
        if (open) {
          setHeight(contentRef.current.scrollHeight);
          // After animation completes, set to auto to handle content changes
          const timer = setTimeout(() => {
            setHeight(undefined);
          }, 300);
          return () => clearTimeout(timer);
        } else {
          // First set fixed height for animation
          setHeight(contentRef.current.scrollHeight);
          // Then animate to 0
          const timer = setTimeout(() => {
            setHeight(0);
          }, 10);
          return () => clearTimeout(timer);
        }
      }
    }
  }, [openItems]);

  return (
    <div
      ref={ref}
      className={cn(
        "text-primary text-sm border-t border-gray-secondary overflow-hidden",
        className,
      )}
      style={{
        height: height === undefined ? "auto" : `${height}px`,
        transition: "height 300ms ease-in-out",
        visibility: height === 0 ? "hidden" : "visible",
      }}
      {...props}
    >
      <div ref={contentRef} className="py-4">
        {children}
      </div>
    </div>
  );
});
AccordionContent.displayName = "AccordionContent";

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
